import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Plus, Monitor, RefreshCw } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { shouldSkipInvalidation } from '@/lib/core/visibility-tracker';
import { toast } from 'sonner';
import { DisplayCard } from './DisplayCard';
import { DisplayPairingDialog } from './DisplayPairingDialog';

interface Display {
  id: string;
  name: string;
  description: string | null;
  device_type: string | null;
  is_active: boolean;
  last_heartbeat_at: string | null;
  orientation: string;
  paired_at: string | null;
  created_at: string;
  business_id: string;
  default_playlist?: {
    id: string;
    name: string;
  } | null;
}

interface DisplayListProps {
  businessId: string;
}

export function DisplayList({ businessId }: DisplayListProps) {
  const [displays, setDisplays] = useState<Display[]>([]);
  const [loading, setLoading] = useState(true);
  const [isPairingOpen, setIsPairingOpen] = useState(false);

  useEffect(() => {
    fetchDisplays();

    // Subscribe to realtime updates for displays and playlist assignments
    const channel = supabase
      .channel(`signage:business:${businessId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'displays',
          filter: `business_id=eq.${businessId}`,
        },
        () => {
          // Skip refresh if tab just became visible (prevents refetch storm)
          if (shouldSkipInvalidation()) return;
          fetchDisplays();
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'display_playlist_assignments',
        },
        () => {
          // Skip refresh if tab just became visible (prevents refetch storm)
          if (shouldSkipInvalidation()) return;
          fetchDisplays();
        }
      )
      .subscribe();

    // Refresh status every 30 seconds
    const refreshInterval = setInterval(fetchDisplays, 30000);

    return () => {
      supabase.removeChannel(channel);
      clearInterval(refreshInterval);
    };
  }, [businessId]);

  const fetchDisplays = async () => {
    try {
      // Fetch displays with their default playlist assignments
      const { data, error } = await supabase
        .from('displays')
        .select(`
          *,
          display_playlist_assignments!left (
            playlist_id,
            is_default,
            signage_playlists (
              id,
              name
            )
          )
        `)
        .eq('business_id', businessId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Transform data to include default_playlist in a cleaner format
      const transformedDisplays = (data || []).map((display: any) => {
        const defaultAssignment = display.display_playlist_assignments?.find(
          (a: any) => a.is_default
        );
        return {
          id: display.id,
          name: display.name,
          description: display.description,
          device_type: display.device_type,
          is_active: display.is_active,
          last_heartbeat_at: display.last_heartbeat_at,
          orientation: display.orientation,
          paired_at: display.paired_at,
          created_at: display.created_at,
          business_id: display.business_id,
          default_playlist: defaultAssignment?.signage_playlists
            ? {
                id: defaultAssignment.signage_playlists.id,
                name: defaultAssignment.signage_playlists.name,
              }
            : null,
        };
      });

      setDisplays(transformedDisplays);
    } catch (err) {
      console.error('Failed to fetch displays:', err);
      toast.error('Failed to load displays');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (displayId: string) => {
    try {
      const { error } = await supabase
        .from('displays')
        .delete()
        .eq('id', displayId);

      if (error) throw error;
      toast.success('Display removed');
      fetchDisplays();
    } catch (err) {
      console.error('Failed to delete display:', err);
      toast.error('Failed to remove display');
    }
  };

  const handleToggleActive = async (displayId: string, isActive: boolean) => {
    try {
      const { error } = await supabase
        .from('displays')
        .update({ is_active: isActive })
        .eq('id', displayId);

      if (error) throw error;
      toast.success(isActive ? 'Display activated' : 'Display deactivated');
      fetchDisplays();
    } catch (err) {
      console.error('Failed to toggle display:', err);
      toast.error('Failed to update display');
    }
  };

  const isOnline = (display: Display): boolean => {
    if (!display.last_heartbeat_at) return false;
    const lastHeartbeat = new Date(display.last_heartbeat_at);
    return Date.now() - lastHeartbeat.getTime() < 60000; // 1 minute
  };

  const onlineCount = displays.filter(isOnline).length;

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <div className="h-8 w-48 bg-muted animate-pulse rounded" />
          <div className="h-10 w-32 bg-muted animate-pulse rounded" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="animate-pulse">
              <CardHeader className="space-y-2">
                <div className="h-5 w-32 bg-muted rounded" />
                <div className="h-4 w-24 bg-muted rounded" />
              </CardHeader>
              <CardContent>
                <div className="h-20 bg-muted rounded" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-xl font-semibold">Your Displays</h2>
          <p className="text-sm text-muted-foreground">
            {displays.length} display{displays.length !== 1 ? 's' : ''} registered
            {displays.length > 0 && ` • ${onlineCount} online`}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchDisplays}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={() => setIsPairingOpen(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Pair New Display
          </Button>
        </div>
      </div>

      {/* Display Grid */}
      {displays.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
              <Monitor className="w-8 h-8 text-muted-foreground" />
            </div>
            <CardTitle className="text-lg mb-2">No displays yet</CardTitle>
            <CardDescription className="text-center max-w-sm mb-4">
              Connect your first TV or display by opening the player URL on your device and entering the pairing code.
            </CardDescription>
            <Button onClick={() => setIsPairingOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Pair Your First Display
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {displays.map((display) => (
            <DisplayCard
              key={display.id}
              display={display}
              isOnline={isOnline(display)}
              onDelete={() => handleDelete(display.id)}
              onToggleActive={(active) => handleToggleActive(display.id, active)}
              onRefresh={fetchDisplays}
            />
          ))}
        </div>
      )}

      {/* Pairing Dialog */}
      <DisplayPairingDialog
        open={isPairingOpen}
        onOpenChange={setIsPairingOpen}
        businessId={businessId}
        onPaired={fetchDisplays}
      />
    </div>
  );
}
